import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { Key, Trash2, StopCircle, Copy, Check, User, Building, Mail, Calendar, CreditCard, EyeOff, Eye, Rocket, LogOut, Edit, Save, X, Phone, Shield, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import StripePopup from '../components/ui/StripePopup.tsx';
import { useStripePopup } from '../hooks/useStripePopup.ts';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

interface License {
  _id: string;
  plan: {
    name: 'UNACTIVE' | 'FREE' | 'PRO' | 'ENTERPRISE';
    displayName: string;
  };
  isActive: boolean;
  issuedAt: string;
  expiresAt?: string;
  paymentStatus: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  createdByStripe: boolean;
  owner: {
    _id: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
  };
  __v: number;
}

interface LicenseKey {
  key: string;
  plan: string;
}

interface AccountProps {
  onNavigate: (page: string) => void;
}

const Account: React.FC<AccountProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { user, token, logout, getUserProfile, updateUserProfile, deleteUserAccount, getUserLicense, getUserLicenseKey, cancelSubscription, createCheckoutSession } = useAuth();
  const { popup, showPopup, hidePopup } = useStripePopup();
  const [licenseKeyCopied, setLicenseKeyCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [licenseKeyVisible, setLicenseKeyVisible] = useState(false);
  const [isRetrievingLicense, setIsRetrievingLicense] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [license, setLicense] = useState<License | null>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [licenseKeyLoading, setLicenseKeyLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    phone: user?.phone || '',
    enterprise: user?.enterprise || '',
    mail2FA: user?.mail2FA || false
  });
  const [is2FAToggling, setIs2FAToggling] = useState(false);
  const [show2FAConfirm, setShow2FAConfirm] = useState(false);
  const [pending2FAState, setPending2FAState] = useState<boolean | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<any>(null);
  const [paymentStatusLoading, setPaymentStatusLoading] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState<string | null>(null);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState<{ plan: string; displayName: string } | null>(null);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        await getUserProfile();
      } catch (error) {
        setProfileError(error instanceof Error ? error.message : 'Failed to fetch profile');
      }
    };

    const fetchLicense = async () => {
      setLicenseLoading(true);
      try {
        const licenseData = await getUserLicense();
        setLicense(licenseData);
      } catch (error) {
        setProfileError(error instanceof Error ? error.message : 'Failed to fetch license');
      } finally {
        setLicenseLoading(false);
      }
    };

    const fetchPaymentStatus = async () => {
      if (!token) return;
      setPaymentStatusLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/payments/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPaymentStatus(data);
        }
      } catch (error) {
        console.error('Error fetching payment status.');
      } finally {
        setPaymentStatusLoading(false);
      }
    };

    if (user) {
      fetchProfile();
      fetchLicense();
      fetchPaymentStatus();
    }
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        phone: user.phone || '',
        enterprise: user.enterprise || '',
        mail2FA: user.mail2FA || false
      });
    }
  }, [user]);

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'FREE': return t('plans.planTypes.free.name');
      case 'PRO': return t('plans.planTypes.premium.name');
      case 'ENTERPRISE': return t('plans.planTypes.enterprise.name');
      case 'UNACTIVE': return 'Unactive';
      default: return plan;
    }
  };

  const getStatusDisplayName = (paymentStatus: string, isActive: boolean) => {
    if (!isActive) return 'inactive';
    switch (paymentStatus) {
      case 'active': return 'active';
      case 'trialing': return 'trial';
      case 'past_due':
      case 'unpaid':
      case 'canceled': return 'inactive';
      default: return 'inactive';
    }
  };

  const userData = {
    name: user ? `${user.firstname} ${user.lastname}` : t('account.sections.profile.fields.name'),
    email: user?.email || t('contact.form.placeholders.email'),
    company: user?.enterprise || t('contact.form.placeholders.company'),
    currentPlan: license?.plan?.displayName || 'Loading...',
    status: license ? getStatusDisplayName(license.paymentStatus, license.isActive) : 'Loading...',
    startDate: license?.issuedAt ? license.issuedAt.split('T')[0] : '',
    endDate: license?.expiresAt ? license.expiresAt.split('T')[0] : '',
    licenseKey: licenseKey || 'Click to retrieve license key'
  };

  const handleCopyLicense = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey);
      setLicenseKeyCopied(true);
      setTimeout(() => setLicenseKeyCopied(false), 2000);
    }
  };

  const handleRetrieveLicense = async () => {
    setIsRetrievingLicense(true);
    setProfileError(null);
    
    try {
      const keyData = await getUserLicenseKey();
      setLicenseKey(keyData.key);
      setLicenseKeyVisible(false);
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : 'Failed to retrieve license key');
    } finally {
      setIsRetrievingLicense(false);
    }
  };

  const handleStopSubscription = async () => {
    setIsCancelling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/payments/subscriptions/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to cancel subscription: ${response.status}`);
      }
      setShowStopConfirm(false);
      
      if (data.message) {
        showPopup(data.message, 'success');
      } else {
        showPopup('Your subscription has been cancelled. It will remain active until the end of your billing period.', 'success');
      }

      if (data.license) {
        setLicense(data.license);
      }
      
      const fetchLicense = async () => {
        try {
          const licenseData = await getUserLicense();
          setLicense(licenseData);
        } catch (error) {
          console.error('Error fetching updated license.');
        }
      };
      const fetchPaymentStatus = async () => {
        if (!token) return;
        try {
          const response = await fetch(`${API_BASE_URL}/payments/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setPaymentStatus(data);
          }
        } catch (error) {
          console.error('Error fetching payment status.');
        }
      };
      fetchLicense();
      fetchPaymentStatus();
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Failed to cancel subscription', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUpgrade = async (plan: 'pro' | 'enterprise') => {
    try {
      setUpgradeLoading(plan);
      const checkoutUrl = await createCheckoutSession(plan);
      window.location.href = checkoutUrl;
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Payment failed', 'error');
    } finally {
      setUpgradeLoading(null);
    }
  };

  const handleDowngradeClick = (targetPlan: 'pro' | 'free') => {
    const displayName = targetPlan === 'pro' ? 'PRO' : 'FREE';
    setPendingDowngrade({ plan: targetPlan, displayName });
    setShowDowngradeConfirm(true);
  };

  const handleDowngrade = async () => {
    if (!pendingDowngrade) return;
    
    setDowngradeLoading(pendingDowngrade.plan);
    try {
      const response = await fetch(`${API_BASE_URL}/payments/downgrade/${pendingDowngrade.plan}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to downgrade: ${response.status}`);
      }

      const data = await response.json();
      setShowDowngradeConfirm(false);
      setPendingDowngrade(null);
      
      if (data.requiresPaymentMethod) {
        if (data.message) {
          showPopup(data.message, 'success');
        }
        if (data.billingPortalUrl) {
          window.location.href = data.billingPortalUrl;
        }
      } else if (data.url) {
        if (data.message) {
          showPopup(data.message, 'success');
        }
        window.location.href = data.url;
      } else if (data.message) {
        showPopup(data.message, 'success');
        
        const fetchLicense = async () => {
          try {
            const licenseData = await getUserLicense();
            setLicense(licenseData);
          } catch (error) {
            console.error('Error fetching updated license.');
          }
        };
        const fetchPaymentStatus = async () => {
          if (!token) return;
          try {
            const response = await fetch(`${API_BASE_URL}/payments/status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const data = await response.json();
              setPaymentStatus(data);
            }
          } catch (error) {
            console.error('Error fetching payment status.');
          }
        };
        fetchLicense();
        fetchPaymentStatus();
      }
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Failed to downgrade subscription', 'error');
    } finally {
      setDowngradeLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!token) {
      showPopup('You must be logged in to manage subscription', 'error');
      return;
    }

    setIsManagingSubscription(true);
    try {
      const currentUrl = window.location.origin + '/account';
      const response = await fetch(`${API_BASE_URL}/payments/customer-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          return_url: currentUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to create portal session: ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Failed to open subscription management', 'error');
      setIsManagingSubscription(false);
    }
  };

  const handleEditProfile = () => {
    setIsEditMode(true);
    setProfileError(null);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setProfileError(null);
    if (user) {
      setProfileData({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        phone: user.phone || '',
        enterprise: user.enterprise || '',
        mail2FA: user.mail2FA || false
      });
    }
  };

  const handleSaveProfile = async () => {
    setIsUpdating(true);
    setProfileError(null);
    
    try {
      await updateUserProfile(profileData);
      setIsEditMode(false);
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfileInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteUserAccount();
      setShowDeleteConfirm(false);
      window.location.replace('/auth');
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : 'Failed to delete account');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      setShowLogoutConfirm(false);
      window.location.replace('/auth');
    } catch (error) {
      setShowLogoutConfirm(false);
      window.location.replace('/auth');
    }
  };

  const handleToggle2FAClick = (enabled: boolean) => {
    setPending2FAState(enabled);
    setShow2FAConfirm(true);
  };

  const confirmToggle2FA = async () => {
    if (pending2FAState === null) return;
    
    setIs2FAToggling(true);
    setProfileError(null);
    setShow2FAConfirm(false);

    try {
      await updateUserProfile({ mail2FA: pending2FAState });
      setProfileData(prev => ({ ...prev, mail2FA: pending2FAState }));
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : t('twoFact.account.errors.updateFailed'));
    } finally {
      setIs2FAToggling(false);
      setPending2FAState(null);
    }
  };

  const cancelToggle2FA = () => {
    setShow2FAConfirm(false);
    setPending2FAState(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case t('account.sections.subscription.status.active'):
        return 'text-green-700 bg-green-100 border-green-200';
      case t('account.sections.subscription.status.inactive'):
        return 'text-red-700 bg-red-100 border-red-200';
      case t('account.sections.subscription.status.trial'):
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <SEO
        title={t('account.seo.title')}
        description={t('account.seo.description')}
        keywords={t('account.seo.keywords')}
      />
      
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('account.title.prefix')}
              <GradientText variant="primary">{t('account.title.highlight')}</GradientText>
              {t('account.title.suffix')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('account.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            <div className="lg:col-span-2 space-y-8">
              
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-secondary to-accent rounded-lg flex items-center justify-center mr-3">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('account.sections.profile.title')}
                    </h2>
                  </div>
                  {!isEditMode ? (
                    <Button
                      onClick={handleEditProfile}
                      variant="outline"
                      size="sm"
                    >
                      Edit
                    </Button>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        onClick={handleSaveProfile}
                        variant="primary"
                        size="sm"
                        leftIcon={<Save size={16} />}
                        disabled={isUpdating}
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        size="sm"
                        disabled={isUpdating}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                
                {profileError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    {profileError}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <User className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          First Name
                        </label>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={profileData.firstname}
                            onChange={(e) => handleProfileInputChange('firstname', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            disabled={isUpdating}
                          />
                        ) : (
                          <p className="text-gray-900 font-medium">{user?.firstname || 'Not set'}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <User className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          Last Name
                        </label>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={profileData.lastname}
                            onChange={(e) => handleProfileInputChange('lastname', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            disabled={isUpdating}
                          />
                        ) : (
                          <p className="text-gray-900 font-medium">{user?.lastname || 'Not set'}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Mail className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          {t('account.sections.profile.fields.email')}
                        </label>
                        <p className="text-gray-900 font-medium">{user?.email || 'Not set'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <User className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          User ID
                        </label>
                        <p className="text-gray-900 font-mono text-sm bg-gray-50 px-2 py-1 rounded border">{user?._id || 'Not available'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Phone className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          Phone
                        </label>
                        {isEditMode ? (
                          <input
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) => handleProfileInputChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            disabled={isUpdating}
                            placeholder="Enter phone number"
                          />
                        ) : (
                          <p className="text-gray-900 font-medium">{user?.phone || 'Not set'}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Building className="w-4 h-4 text-gray-400 mt-2" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-500 mb-1">
                          {t('account.sections.profile.fields.company')}
                        </label>
                        {isEditMode ? (
                          <input
                            type="text"
                            value={profileData.enterprise}
                            onChange={(e) => handleProfileInputChange('enterprise', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            disabled={isUpdating}
                            placeholder="Enter company name"
                          />
                        ) : (
                          <p className="text-gray-900 font-medium">{
                            user?.enterprise && user.enterprise !== 'false' && user.enterprise.trim() !== '' 
                              ? user.enterprise 
                              : 'Not set'
                          }</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-secondary to-accent rounded-lg flex items-center justify-center mr-3">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('account.sections.subscription.title')}
                    </h2>
                  </div>
                  
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-500">
                      {t('account.sections.subscription.fields.plan')}
                    </label>
                    <p className="text-lg font-semibold text-gray-900">{userData.currentPlan}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-500">
                      {t('account.sections.subscription.fields.status')}
                    </label>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(userData.status)}`}>
                      {t(`account.sections.subscription.status.${userData.status.toLowerCase()}`)}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-500">
                      {t('account.sections.subscription.fields.startDate')} - {t('account.sections.subscription.fields.endDate')}
                    </label>
                    <p className="text-gray-900 font-medium">
                      {new Date(userData.startDate).toLocaleDateString()} - {new Date(userData.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>


                {paymentStatusLoading ? (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-500">Loading payment status...</div>
                  </div>
                ) : paymentStatus && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Payment Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Status: </span>
                        <span className="font-medium">{paymentStatus.status || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Last Payment: </span>
                        <span className="font-medium">
                          {paymentStatus.lastPayment ? new Date(paymentStatus.lastPayment).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}


                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {licenseLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="text-sm text-gray-500">Loading license information...</div>
                    </div>
                  ) : !licenseKey ? (
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('account.sections.subscription.fields.licenseKey')}
                      </label>
                      <Button
                        onClick={handleRetrieveLicense}
                        variant="primary"
                        size="sm"
                        leftIcon={<Key size={16} />}
                        disabled={isRetrievingLicense || !license}
                      >
                        {isRetrievingLicense ? t('account.sections.subscription.buttons.retrievingLicense') : t('account.sections.subscription.buttons.retrieveLicense')}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          {t('account.sections.subscription.fields.licenseKey')}
                        </label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type={licenseKeyVisible ? "text" : "password"}
                          value={licenseKey || ''}
                          readOnly
                          className="flex-1 bg-white rounded-lg px-4 py-3 text-gray-900 font-mono text-sm border border-gray-200 focus:outline-none"
                        />
                        {licenseKey && (
                          <>
                            <button
                              onClick={handleCopyLicense}
                              className="flex items-center justify-center w-10 h-10 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors duration-200"
                              title={t('account.sections.subscription.buttons.copyLicense')}
                            >
                              {licenseKeyCopied ? (
                                <Check size={16} className="text-green-600" />
                              ) : (
                                <Copy size={16} className="text-gray-600" />
                              )}
                            </button>
                            <button
                              onClick={() => setLicenseKeyVisible(!licenseKeyVisible)}
                              className="flex items-center justify-center w-10 h-10 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors duration-200"
                              title={licenseKeyVisible ? "Hide license key" : "Show license key"}
                            >
                              {licenseKeyVisible ? <EyeOff size={16} className="text-gray-600" /> : <Eye size={16} className="text-gray-600" />}
                            </button>
                            <button
                              onClick={() => {
                                setLicenseKeyVisible(false);
                                setLicenseKey(null);
                              }}
                              className="flex items-center justify-center w-10 h-10 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors duration-200"
                              title="Clear license key"
                            >
                              <X size={16} className="text-gray-600" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-secondary to-accent rounded-lg flex items-center justify-center mr-3">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('twoFact.account.security.title')}
                  </h2>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {t('twoFact.account.security.email2fa.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('twoFact.account.security.email2fa.description')}
                      </p>
                    </div>
                    <div className="ml-4 flex items-center space-x-3">
                      <span className={`text-sm font-medium ${profileData.mail2FA ? 'text-accent' : 'text-gray-500'}`}>
                        {profileData.mail2FA ? 'Enabled' : 'Disabled'}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={profileData.mail2FA}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            handleToggle2FAClick(enabled);
                          }}
                          disabled={is2FAToggling}
                          className="sr-only"
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/25 rounded-full peer transition-colors duration-200 ${
                          profileData.mail2FA ? 'bg-accent' : 'bg-gray-300'
                        } ${is2FAToggling ? 'opacity-50' : ''}`}>
                          <div className={`absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform duration-200 ${
                            profileData.mail2FA ? 'translate-x-5' : 'translate-x-0'
                          }`}></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              
              <div className="bg-gradient-to-br from-primary to-secondary rounded-xl shadow-lg p-6 text-white animate-fade-in">
                <h3 className="text-lg font-semibold mb-4">{t('account.sections.overview.title')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">{t('account.sections.overview.fields.currentPlan')}</span>
                    <span className="font-semibold">{userData.currentPlan}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">{t('account.sections.overview.fields.status')}</span>
                    <span className="bg-white/20 px-2 py-1 rounded-full text-xs font-medium">
                      {t(`account.sections.subscription.status.${userData.status.toLowerCase()}`)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">{t('account.sections.overview.fields.renewalDate')}</span>
                    <span className="font-semibold">{new Date(userData.endDate).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {license?.plan && (
                  <div className="mt-4 pt-4 border-t border-white/20 space-y-2">
                    {(license.plan.name === 'FREE' || license.plan.name === 'PRO') && (
                      <Button
                        onClick={() => onNavigate('plans')}
                        variant="cta-glass"
                        size="sm"
                        className="w-full bg-white/10 hover:bg-white/20 border-white/30 text-white"
                        leftIcon={<ChevronUp size={16} className="text-white" />}
                      >
{t('account.sections.subscription.buttons.upgrade')}
                      </Button>
                    )}
                    
                    {license.plan.name === 'ENTERPRISE' && (
                      <Button
                        onClick={() => handleDowngradeClick('pro')}
                        variant="cta-glass"
                        size="sm"
                        className="w-full bg-white/10 hover:bg-white/20 border-white/30 text-white"
                        leftIcon={<ChevronDown size={16} className="text-white" />}
                        disabled={downgradeLoading === 'pro'}
                      >
{downgradeLoading === 'pro' ? 'Processing...' : t('account.sections.subscription.buttons.downgradeToPro')}
                      </Button>
                    )}
                    
                    {license.plan.name === 'PRO' && (
                      <Button
                        onClick={() => handleDowngradeClick('free')}
                        variant="cta-glass"
                        size="sm"
                        className="w-full bg-white/10 hover:bg-white/20 border-white/30 text-white"
                        leftIcon={<ChevronDown size={16} className="text-white" />}
                        disabled={downgradeLoading === 'free'}
                      >
{downgradeLoading === 'free' ? 'Processing...' : t('account.sections.subscription.buttons.downgradeToFree')}
                      </Button>
                    )}
                    
                    {license.createdByStripe && (
                      <Button
                        onClick={handleManageSubscription}
                        variant="cta-glass"
                        size="sm"
                        className="w-full bg-white/10 hover:bg-white/20 border-white/30 text-white"
                        leftIcon={<Settings size={16} className="text-white" />}
                        disabled={isManagingSubscription}
                      >
{isManagingSubscription ? 'Opening portal...' : t('account.sections.subscription.buttons.manageSubscription')}
                      </Button>
                    )}
                  </div>
                )}
                
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('account.sections.actions.title')}</h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => onNavigate('deploy')}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    leftIcon={<Rocket size={16} />}
                  >
                    {t('account.sections.actions.buttons.deployKexa')}
                  </Button>
                  <Button
                    onClick={() => setShowLogoutConfirm(true)}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-orange-300 text-orange-700 hover:border-orange-500 hover:text-orange-500"
                    leftIcon={<LogOut size={16} />}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? 'Logging out...' : t('account.sections.actions.buttons.logout')}
                  </Button>
                  
                  <Button
                    onClick={() => onNavigate('contact')}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    leftIcon={<Mail size={16} />}
                  >
                    {t('account.sections.actions.buttons.contactSupport')}
                  </Button>
                  
                  <div className="border-t border-gray-200 my-8"></div>
                  
                  <div className="space-y-3">
                    <Button
                      onClick={() => setShowStopConfirm(true)}
                      variant="danger-outline"
                      size="sm"
                      className="w-full justify-start"
                      leftIcon={<StopCircle size={16} />}
                      disabled={isCancelling || !license?.plan || license.plan.name === 'FREE'}
                    >
                      {isCancelling ? 'Cancelling...' : t('account.sections.subscription.buttons.stopSubscription')}
                    </Button>
                    
                    <Button
                      onClick={() => setShowDeleteConfirm(true)}
                      variant="danger-outline"
                      size="sm"
                      className="w-full justify-start"
                      leftIcon={<Trash2 size={16} />}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : t('account.sections.actions.buttons.deleteAccount')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showStopConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slide-up">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('account.sections.actions.confirmations.stopSubscription.title')}</h3>
            <p className="text-gray-600 mb-6">
              {t('account.sections.actions.confirmations.stopSubscription.message')}
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={handleStopSubscription}
                variant="warning"
                className="flex-1"
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : t('account.sections.actions.confirmations.stopSubscription.confirm')}
              </Button>
              <Button
                onClick={() => setShowStopConfirm(false)}
                variant="secondary"
                className="flex-1"
              >
                {t('account.sections.actions.confirmations.stopSubscription.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slide-up">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('account.sections.actions.confirmations.logout.title')}</h3>
            <p className="text-gray-600 mb-6">
              {t('account.sections.actions.confirmations.logout.message')}
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={handleLogout}
                variant="warning"
                className="flex-1"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Logging out...' : t('account.sections.actions.confirmations.logout.confirm')}
              </Button>
              <Button
                onClick={() => setShowLogoutConfirm(false)}
                variant="secondary"
                className="flex-1"
                disabled={isLoggingOut}
              >
                {t('account.sections.actions.confirmations.logout.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slide-up">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('account.sections.actions.confirmations.deleteAccount.title')}</h3>
            <p className="text-gray-600 mb-6">
              {t('account.sections.actions.confirmations.deleteAccount.message')}
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={handleDeleteAccount}
                variant="danger"
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : t('account.sections.actions.confirmations.deleteAccount.confirm')}
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="secondary"
                className="flex-1"
                disabled={isDeleting}
              >
                {t('account.sections.actions.confirmations.deleteAccount.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {show2FAConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slide-up">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {pending2FAState 
                ? t('twoFact.account.confirmations.enable2fa.title')
                : t('twoFact.account.confirmations.disable2fa.title')
              }
            </h3>
            <p className="text-gray-600 mb-6">
              {pending2FAState
                ? t('twoFact.account.confirmations.enable2fa.message')
                : t('twoFact.account.confirmations.disable2fa.message')
              }
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={confirmToggle2FA}
                variant={pending2FAState ? "primary" : "warning"}
                className="flex-1"
                disabled={is2FAToggling}
              >
                {is2FAToggling 
                  ? (pending2FAState ? t('twoFact.account.buttons.enabling') : t('twoFact.account.buttons.disabling'))
                  : (pending2FAState 
                      ? t('twoFact.account.confirmations.enable2fa.confirm')
                      : t('twoFact.account.confirmations.disable2fa.confirm')
                    )
                }
              </Button>
              <Button
                onClick={cancelToggle2FA}
                variant="secondary"
                className="flex-1"
                disabled={is2FAToggling}
              >
                {pending2FAState 
                  ? t('twoFact.account.confirmations.enable2fa.cancel')
                  : t('twoFact.account.confirmations.disable2fa.cancel')
                }
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDowngradeConfirm && pendingDowngrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slide-up">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Downgrade to {pendingDowngrade.displayName}
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to downgrade to the {pendingDowngrade.displayName} plan? Your current plan will remain active until the end of your billing period, and then you'll be automatically moved to the {pendingDowngrade.displayName} plan.
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={handleDowngrade}
                variant="warning"
                className="flex-1"
                disabled={downgradeLoading === pendingDowngrade.plan}
              >
                {downgradeLoading === pendingDowngrade.plan ? 'Processing...' : `Downgrade to ${pendingDowngrade.displayName}`}
              </Button>
              <Button
                onClick={() => {
                  setShowDowngradeConfirm(false);
                  setPendingDowngrade(null);
                }}
                variant="secondary"
                className="flex-1"
                disabled={downgradeLoading === pendingDowngrade.plan}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <StripePopup
        isOpen={popup.isOpen}
        onClose={hidePopup}
        message={popup.message}
        type={popup.type}
      />
    </div>
  );
};

export default Account;